import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Home from "@/pages/Home";
import Admin from "@/pages/Admin";
import SeoLandingPage from "@/pages/SeoLandingPage";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import Funnel from "@/pages/Funnel";
import PartnerRecruit from "@/pages/PartnerRecruit";
import PartnerPortal from "@/pages/PartnerPortal";
import FirmPortal from "@/pages/FirmPortal";
import FirmCard from "@/pages/FirmCard";
import Firms from "@/pages/Firms";
import OkresyPage from "@/pages/OkresyPage";
import OkresPage from "@/pages/OkresPage";
import Partners from "@/pages/Partners";
import Vizualizacia from "@/pages/Vizualizacia";
import Galeria from "@/pages/Galeria";
import Premeny from "@/pages/Premeny";
import Normy from "@/pages/Normy";
import Kontakt from "@/pages/Kontakt";
import Rezervacia from "@/pages/Rezervacia";
import ManageBooking from "@/pages/ManageBooking";
import Faq from "@/pages/Faq";
import MaterialCompare from "@/pages/MaterialCompare";
import PlasterCompare from "@/pages/PlasterCompare";
import Podhlady from "@/pages/Podhlady";
import SubmitReview from "@/pages/SubmitReview";
import Legal from "@/pages/Legal";
import Objednavka from "@/pages/Objednavka";
import CookieConsent from "@/components/CookieConsent";
import { track, api, getVisitor } from "@/lib/api";
import { registerServiceWorker } from "@/lib/push";
import ChatbotWidget from "@/components/ChatbotWidget";
import "@/App.css";

function SeoRobots() {
  // Interné/ad stránky držíme mimo indexu — Google ich môže crawlovať a vidieť noindex.
  const NOINDEX = ["/admin", "/partner", "/firma-portal", "/spravovat-rezervaciu", "/hodnotenie", "/funnel/"];
  const loc = useLocation();
  useEffect(() => {
    const noindex = NOINDEX.some((p) => loc.pathname.startsWith(p));
    let el = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", "robots"); document.head.appendChild(el); }
      el.setAttribute("content", "noindex, nofollow");
    } else if (el) {
      el.remove();
    }
  }, [loc.pathname]);
  return null;
}

function Presence() {
  const loc = useLocation();
  useEffect(() => {
    if (loc.pathname.startsWith("/admin")) return;
    const ping = () => {
      if (document.visibilityState !== "visible") return;
      if (localStorage.getItem("z_cookie_consent") !== "accepted") return;
      const { id } = getVisitor();
      api.post("/presence/ping", { visitor_id: id, path: loc.pathname }).catch(() => {});
    };
    ping();
    const iv = setInterval(ping, 25000);
    document.addEventListener("visibilitychange", ping);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", ping); };
  }, [loc.pathname]);
  return null;
}

function Tracker() {
  const loc = useLocation();
  useEffect(() => {
    if (loc.pathname.startsWith("/admin")) return;
    track("visit", "", loc.pathname);
    window.__lenis?.scrollTo(0, { immediate: true });

    // Scroll-depth tracking: report the deepest scroll % reached on this page.
    let maxDepth = 0;
    const compute = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      const pct = scrollable > 0 ? Math.round((window.scrollY / scrollable) * 100) : 100;
      if (pct > maxDepth) maxDepth = Math.min(100, pct);
    };
    const onScroll = () => compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    compute();

    const flush = () => {
      if (maxDepth > 0) {
        track("scroll_depth", loc.pathname, loc.pathname, maxDepth);
        track("exit", loc.pathname, loc.pathname);
        maxDepth = 0;
      }
    };
    const onHide = () => { if (document.visibilityState === "hidden") flush(); };
    document.addEventListener("visibilitychange", onHide);

    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onHide);
      flush();
    };
  }, [loc.pathname]);
  return null;
}

function App() {
  useEffect(() => {
    registerServiceWorker().catch(() => {});
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App bg-ink min-h-screen">
      <div className="noise-overlay" />
      <Toaster theme="dark" position="top-center" richColors />
      <BrowserRouter>
        <Tracker />
        <Presence />
        <SeoRobots />
        <CookieConsent />
        <ChatbotWidget />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/partner" element={<PartnerPortal />} />
          <Route path="/firma-portal" element={<FirmPortal />} />
          <Route path="/realizatori" element={<Firms />} />
          <Route path="/okresy" element={<OkresyPage />} />
          <Route path="/okres/:slug" element={<OkresPage />} />
          <Route path="/firma/:slug" element={<FirmCard />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/funnel/:type" element={<Funnel />} />
          <Route path="/stat-sa-partnerom" element={<PartnerRecruit />} />
          <Route path="/partneri" element={<Partners />} />
          <Route path="/vizualizacia" element={<Vizualizacia />} />
          <Route path="/galeria" element={<Galeria />} />
          <Route path="/premeny" element={<Premeny />} />
          <Route path="/normy" element={<Normy />} />
          <Route path="/kontakt" element={<Kontakt />} />
          <Route path="/rezervacia" element={<Rezervacia />} />
          <Route path="/spravovat-rezervaciu" element={<ManageBooking />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/materialy" element={<MaterialCompare />} />
          <Route path="/omietky" element={<PlasterCompare />} />
          <Route path="/podhlady" element={<Podhlady />} />
          <Route path="/hodnotenie" element={<SubmitReview />} />
          <Route path="/ochrana-osobnych-udajov" element={<Legal type="gdpr" />} />
          <Route path="/obchodne-podmienky" element={<Legal type="terms" />} />
          <Route path="/pouzivanie-cookies" element={<Legal type="cookies" />} />
          <Route path="/reklamacny-poriadok" element={<Legal type="complaints" />} />
          <Route path="/zmluva-o-dielo" element={<Legal type="contract" />} />
          <Route path="/odstupenie-od-zmluvy" element={<Legal type="withdrawal" />} />
          <Route path="/objednavka" element={<Objednavka />} />
          <Route path="/:kw/:town" element={<SeoLandingPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
